/**
 * ScrollNavLayout — composable scroll-spy sidebar + content layout.
 *
 * Usage:
 * ```tsx
 * <ScrollNavLayout sectionIds={["profile", "account"]} navLabel="Settings">
 *   <ScrollNavLayout.Nav>
 *     <ScrollNavLayout.NavItem id="profile" label="Profile" icon={IconUser} />
 *     <ScrollNavLayout.NavItem id="account" label="Account" icon={IconSettings} />
 *   </ScrollNavLayout.Nav>
 *
 *   <ScrollNavLayout.Content>
 *     <ScrollNavLayout.Section id="profile">
 *       <ProfileSection />
 *     </ScrollNavLayout.Section>
 *     <ScrollNavLayout.Section id="account">
 *       <AccountSection />
 *     </ScrollNavLayout.Section>
 *   </ScrollNavLayout.Content>
 * </ScrollNavLayout>
 * ```
 */

import { useScrollSpy } from "@/hooks/useScrollSpy"
import { cn } from "@/lib/utils"
import React, { createContext, useContext, useRef, type ReactNode } from "react"

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

interface ScrollNavContextValue {
  activeId: string
  scrollTo: (id: string) => void
  containerRef: React.RefObject<HTMLDivElement | null>
}

const ScrollNavContext = createContext<ScrollNavContextValue | null>(null)

function useScrollNavContext() {
  const ctx = useContext(ScrollNavContext)
  if (!ctx) {
    throw new Error(
      "ScrollNavLayout sub-components must be used inside <ScrollNavLayout>",
    )
  }
  return ctx
}

// ---------------------------------------------------------------------------
// Root
// ---------------------------------------------------------------------------

interface ScrollNavLayoutProps {
  /** IDs that match the `id` props of <ScrollNavLayout.Section> children. */
  sectionIds: string[]
  children: ReactNode
  className?: string
}

function ScrollNavLayoutRoot({
  sectionIds,
  children,
  className,
}: ScrollNavLayoutProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const { activeId, scrollTo } = useScrollSpy(sectionIds, containerRef)

  return (
    <ScrollNavContext.Provider value={{ activeId, scrollTo, containerRef }}>
      <div className={cn("flex h-full flex-col overflow-hidden", className)}>
        <div className="flex min-h-0 flex-1 flex-col lg:flex-row">
          {children}
        </div>
      </div>
    </ScrollNavContext.Provider>
  )
}

// ---------------------------------------------------------------------------
// Nav panel  (desktop: vertical sticky sidebar | mobile: horizontal tab bar)
// ---------------------------------------------------------------------------

interface NavProps {
  children: ReactNode
  /** `aria-label` for both nav elements. Defaults to "Navigation". */
  label?: string
  /** Extra classes for the desktop sidebar wrapper. */
  sidebarClassName?: string
  /** Extra classes for the mobile tab bar wrapper. */
  tabBarClassName?: string
}

function Nav({
  children,
  label = "Navigation",
  sidebarClassName,
  tabBarClassName,
}: NavProps) {
  return (
    <div className="shrink-0 px-6 pt-4 lg:p-8">
      {/* Desktop: vertical sticky sidebar */}
      <nav
        className={cn(
          "sticky top-0 hidden w-48 shrink-0 lg:block",
          sidebarClassName,
        )}
        role="navigation"
        aria-label={label}
      >
        <ul className="space-y-1">{children}</ul>
      </nav>

      {/* Mobile: horizontal scrollable tab bar */}
      <nav
        className={cn(
          "overflow-x-auto border-b pb-2 lg:hidden",
          tabBarClassName,
        )}
        role="navigation"
        aria-label={label}
      >
        <ul className="flex gap-1">{children}</ul>
      </nav>
    </div>
  )
}

// ---------------------------------------------------------------------------
// NavItem
// ---------------------------------------------------------------------------

interface NavItemProps {
  id: string
  label: ReactNode
  icon?: React.ComponentType<{ size?: number | string; className?: string }>
  className?: string
}

function NavItem({ id, label, icon: Icon, className }: NavItemProps) {
  const { activeId, scrollTo } = useScrollNavContext()
  const isActive = activeId === id

  return (
    <>
      {/* Rendered inside the desktop <ul> */}
      <li className="hidden lg:block">
        <button
          onClick={() => scrollTo(id)}
          aria-current={isActive ? "page" : undefined}
          className={cn(
            "flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors",
            isActive
              ? "bg-primary/10 text-primary"
              : "text-muted-foreground hover:bg-muted hover:text-foreground",
            className,
          )}
        >
          {Icon && <Icon size={18} />}
          {label}
        </button>
      </li>

      {/* Rendered inside the mobile <nav> */}
      <li className="shrink-0 lg:hidden">
        <button
          onClick={() => scrollTo(id)}
          aria-current={isActive ? "page" : undefined}
          className={cn(
            "flex items-center gap-2 rounded-md px-3 py-2 text-sm transition-colors",
            isActive
              ? "bg-primary/10 text-primary"
              : "text-muted-foreground hover:bg-muted hover:text-foreground",
            className,
          )}
        >
          {Icon && <Icon size={16} />}
          {label}
        </button>
      </li>
    </>
  )
}

// ---------------------------------------------------------------------------
// Content  (the scrollable right-hand panel)
// ---------------------------------------------------------------------------

interface ContentProps {
  children: ReactNode
  /** Max-width wrapper class. Defaults to "max-w-2xl". */
  maxWidthClassName?: string
  className?: string
}

function Content({
  children,
  maxWidthClassName = "max-w-2xl",
  className,
}: ContentProps) {
  const { containerRef } = useScrollNavContext()

  return (
    <div
      ref={containerRef as React.RefObject<HTMLDivElement>}
      className={cn("flex-1 overflow-y-auto p-6 lg:p-8", className)}
    >
      <div className={cn("mx-auto space-y-8", maxWidthClassName)}>
        {children}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Section  (a content region that the scroll-spy observes)
// ---------------------------------------------------------------------------

interface SectionProps {
  id: string
  children: ReactNode
  className?: string
}

function Section({ id, children, className }: SectionProps) {
  return (
    <section id={id} className={className}>
      {children}
    </section>
  )
}

// ---------------------------------------------------------------------------
// Compose sub-components onto the root
// ---------------------------------------------------------------------------

export const ScrollNavLayout = Object.assign(ScrollNavLayoutRoot, {
  Nav,
  NavItem,
  Content,
  Section,
})
